import { useState, useEffect } from 'react';
import { Button, Card, Table, Tag, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import demoModulesApi, { DemoModule } from '../../lib/demoModulesApi';
import ModuleConfigModal from './ModuleConfigModal';

const ModuleBuilderPage = () => {
  const [modules, setModules] = useState<DemoModule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingModule, setEditingModule] = useState<DemoModule | null>(null);
  const navigate = useNavigate();

  const loadModules = async () => {
    setLoading(true);
    try {
      const data = await demoModulesApi.getModules();
      setModules(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  const handleCreate = () => {
    setEditingModule(null);
    setModalVisible(true);
  };

  const handleEdit = (module: DemoModule) => {
    setEditingModule(module);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await demoModulesApi.deleteModule(id);
      message.success('Module deleted successfully');
      loadModules();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete module');
    }
  };

  const handleModalClose = (reload?: boolean) => {
    setModalVisible(false);
    setEditingModule(null);
    if (reload) {
      loadModules();
    }
  };

  const categoryColors: Record<string, string> = {
    work: 'blue',
    asset: 'green',
    inventory: 'orange',
    service: 'purple',
    admin: 'red',
  };

  const columns = [
    {
      title: 'Module Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: DemoModule) => (
        <Space>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: record.color || '#ec4899',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <AppstoreOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.description}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (text: string) => <code style={{ padding: '2px 6px', background: '#f0f0f0', borderRadius: 4 }}>{text}</code>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color={categoryColors[category] || 'default'}>
          {category.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Fields',
      key: 'fields',
      render: (_: any, record: DemoModule) => (
        <Tag>{record._count?.fields || 0} fields</Tag>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: DemoModule) => (
        <Space>
          {record.isEnabled && <Tag color="success">Enabled</Tag>}
          {!record.isEnabled && <Tag color="error">Disabled</Tag>}
          {!record.isVisible && <Tag>Hidden</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: DemoModule) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/demo/module-builder/${record.id}/fields`)}
          >
            Fields
          </Button>
          <Button
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete module?"
            description="This will delete all fields and records. This cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0 }}>Module Builder</h2>
            <p style={{ margin: '4px 0 0', color: '#666' }}>
              Create and configure Maximo modules
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create Module
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={modules}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <ModuleConfigModal
        visible={modalVisible}
        module={editingModule}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default ModuleBuilderPage;
